import { ModuleWrapper } from '@/components'
import { SyncButton } from '@/components_deprecated/Button'
import { PageType } from '@/modules/common/consts'
import { ALL_USER_EXPORTS_QUERY } from '@/modules/exports/queries'
import { useInfiniteScroll, useNodes } from '@/utils/hooks'
import { useBreadcrumbs } from '@/utils/hooks/useBreadcrumbs'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { UserExportsTable } from './UserExportsTable'

interface UserExportsProps {}

export const UserExports: React.FC<UserExportsProps> = () => {
  const translations = useTranslate({
    userExports: 'exports.user-exports',
  })

  const [lastVisit, setLastVisit] = useState<string>()

  const { setOverrides, overrides } = useBreadcrumbs()

  const {
    data: userExportsData,
    loading: userExportsLoading,
    refetch: refetchUserExports,
    fetchMore: fetchMoreUserExports,
  } = useQuery(ALL_USER_EXPORTS_QUERY, {
    variables: {
      first: DEFAULT_PAGE_SIZE,
      orderBy: '-createdAt',
    },
    pollInterval: 2500,
    // We need this for the sync animation to trigger
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      setLastVisit(
        lastVisit ?? (localStorage.getItem('userExportsLastVisit') || '')
      )
      localStorage.setItem('userExportsLastVisit', formatISO(new Date()))
    },
  })

  const userExports = useNodes(userExportsData?.allUserExports)
  const pageInfo = userExportsData?.allUserExports.pageInfo
  const isFirstLoad = !userExportsData

  const handleScrollBottom = useCallback(async () => {
    if (
      typeof userExportsData === 'undefined' ||
      typeof pageInfo === 'undefined'
    )
      return
    if (!pageInfo.hasNextPage) return
    if (!pageInfo.endCursor) return
    try {
      await fetchMoreUserExports({
        variables: {
          first: DEFAULT_PAGE_SIZE,
          orderBy: '-createdAt',
          after: pageInfo.endCursor,
        },
      })
    } catch (e) {
      console.error(e)
    }
  }, [userExportsData, fetchMoreUserExports, pageInfo])

  useInfiniteScroll(
    handleScrollBottom,
    300,
    !userExportsLoading && pageInfo?.hasNextPage
  )

  useEffect(() => {
    if (overrides['user-exports']) return
    setOverrides({ 'user-exports': translations.userExports })
  }, [overrides, setOverrides, translations.userExports])

  function dateifyLastVisit() {
    const date = new Date(lastVisit ?? '')
    return isNaN(date.getTime()) ? new Date() : date
  }

  return (
    <ModuleWrapper
      title={translations.userExports}
      page={PageType.EXPORTS}
      headerContent={
        <SyncButton
          background="white"
          reset={userExportsLoading}
          refetchQuery={refetchUserExports}
        />
      }
    >
      <UserExportsTable
        userExports={userExports}
        loading={userExportsLoading}
        isFirstLoad={isFirstLoad}
        lastVisit={dateifyLastVisit()}
      />
    </ModuleWrapper>
  )
}
